import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import BranchSubNav from "@/components/branches/BranchSubNav";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import {
  Edit2,
  Shield,
  Users,
  MapPin,
  Phone,
  Mail,
  Clock,
  Settings,
  Building2,
} from "lucide-react";
import { branchesApi, type Branch } from "@/lib/api/branches";
import { cn } from "@/lib/utils";

const BranchOverviewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { isTenantAdmin } = useAccessControl();
  const [branch, setBranch] = useState<Branch | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) loadBranch();
  }, [id]);

  const loadBranch = async () => {
    try {
      setLoading(true);
      const data = await branchesApi.getById(id!);
      setBranch(data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || "Failed to load branch.");
      navigate("/setup/branches");
    } finally {
      setLoading(false);
    }
  };

  const navCards = [
    {
      label: "Manage Staff",
      description: "View staff assigned to this branch",
      icon: Users,
      color: "text-primary",
      bg: "bg-primary/5",
      path: `/setup/branches/${id}/staff`,
    },
    {
      label: "Operating Hours",
      description: "Configure weekly schedule & closures",
      icon: Clock,
      color: "text-green-600",
      bg: "bg-green-50",
      path: `/setup/branches/${id}/hours`,
    },
    {
      label: "Branch Settings",
      description: "Loan durations, fine overrides & policies",
      icon: Settings,
      color: "text-purple-600",
      bg: "bg-purple-50",
      path: `/setup/branches/${id}/settings`,
    },
    {
      label: "Permissions",
      description: "Control what staff can do here",
      icon: Shield,
      color: "text-amber-600",
      bg: "bg-amber-50",
      path: `/setup/branches/${id}/permissions`,
    },
  ];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <LoadingSpinner size="lg" />
        </div>
      </AppLayout>
    );
  }

  if (!branch) return null;

  return (
    <AppLayout>
      <BranchSubNav branch={branch} />

      <PageContainer width="wide">
        <PageHeader
          title={branch.name}
          description={`Branch code: ${branch.code}`}
          breadcrumbItems={[
            { label: "Setup", href: "/setup" },
            { label: "Branches", href: "/setup/branches" },
            { label: branch.name },
          ]}
          showSearch={false}
          titleSize="md"
          breadcrumbAlign="start"
          preserveCase
          actions={
            isTenantAdmin ? (
              <Button
                onClick={() => navigate(`/setup/branches/${id}/edit`)}
                variant="outline"
                className="gap-2"
              >
                <Edit2 className="w-4 h-4" />
                Edit Branch
              </Button>
            ) : undefined
          }
        />

        {/* Stats row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">{branch._count?.staff ?? 0}</p>
            <p className="text-sm text-gray-500 mt-1">Staff Members</p>
          </Card>
          <Card className="p-4 text-center">
            <p className="text-3xl font-bold text-gray-900">
              {branch.permissions.length === 0 ? "All" : branch.permissions.length}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {branch.permissions.length === 0 ? "Permissions (unrestricted)" : "Permissions set"}
            </p>
          </Card>
          <Card className="p-4 text-center">
            <div
              className={cn(
                "inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium mt-1",
                branch.status === "ACTIVE"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              )}
            >
              <span
                className={cn(
                  "w-2 h-2 rounded-full",
                  branch.status === "ACTIVE" ? "bg-green-500" : "bg-red-500"
                )}
              />
              {branch.status}
            </div>
            <p className="text-sm text-gray-500 mt-1">Status</p>
          </Card>
          <Card className="p-4 text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium mt-1 bg-amber-100 text-amber-700">
              {branch.isMain ? "Main Library" : "Branch"}
            </div>
            <p className="text-sm text-gray-500 mt-1">Type</p>
          </Card>
        </div>

        {/* Contact / location info */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
              Location
            </h2>
            <div className="space-y-3 text-sm text-gray-700">
              {branch.address || branch.city || branch.state ? (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                  <span>
                    {[branch.address, branch.city, branch.state, branch.country, branch.postalCode]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </div>
              ) : (
                <p className="text-gray-400 italic">No address recorded</p>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
              Contact
            </h2>
            <div className="space-y-3 text-sm text-gray-700">
              {branch.phone ? (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span>{branch.phone}</span>
                </div>
              ) : null}
              {branch.email ? (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a href={`mailto:${branch.email}`} className="hover:underline text-primary">
                    {branch.email}
                  </a>
                </div>
              ) : null}
              {!branch.phone && !branch.email && (
                <p className="text-gray-400 italic">No contact info recorded</p>
              )}
            </div>
          </Card>
        </div>

        {/* Quick-nav cards */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
            Branch Management
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {navCards.map((card) => (
              <button
                key={card.label}
                onClick={() => navigate(card.path)}
                className="text-left p-5 rounded-lg border border-gray-200 bg-white hover:shadow-md hover:border-gray-300 transition-all group"
              >
                <div
                  className={cn(
                    "w-10 h-10 rounded-lg flex items-center justify-center mb-3",
                    card.bg
                  )}
                >
                  <card.icon className={cn("w-5 h-5", card.color)} />
                </div>
                <p className="font-semibold text-gray-900 group-hover:text-primary transition-colors">
                  {card.label}
                </p>
                <p className="text-xs text-gray-500 mt-1">{card.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Metadata */}
        <Card className="p-6">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
            Branch Info
          </h2>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <dt className="text-gray-500">Created</dt>
              <dd className="font-medium mt-1">
                {new Date(branch.createdAt).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Last updated</dt>
              <dd className="font-medium mt-1">
                {new Date(branch.updatedAt).toLocaleDateString()}
              </dd>
            </div>
            <div>
              <dt className="text-gray-500">Branch ID</dt>
              <dd className="font-mono text-xs mt-1 break-all text-gray-600">{branch.id}</dd>
            </div>
            <div>
              <dt className="text-gray-500">Permissions mode</dt>
              <dd className="font-medium mt-1">
                {branch.permissions.length === 0 ? "Unrestricted (inherits all)" : "Restricted"}
              </dd>
            </div>
          </dl>
        </Card>
      </PageContainer>
    </AppLayout>
  );
};

export default BranchOverviewPage;
